// src/components/DevTools/DevTools.tsx

'use client'

import React, { useState, useEffect, useRef } from 'react'
import {
  Monitor,
  Smartphone,
  RotateCcw,
  X,
  Moon,
  Sun,
  Grid,
  Minimize2,
  MoreHorizontal,
  RefreshCw,
  Accessibility,
} from 'lucide-react'

import {
  Device,
  DevToolsProps,
  EnhancedLighthouseMetrics,
  DetailedAccessibilityIssue,
  ComprehensiveSEOMetrics,
  SecurityMetrics,
} from '../../types'
import { devices, essentialDevices } from '../../data/devices'
import { useDevToolsTests } from '../../hooks/useDevToolsTests'
import { getCleanUrl, refreshAllFrames } from '../../utils/helpers'
import { DevicePreview } from './DevicePreview'
import { MetricsPanel } from './MetricsPanel'
import { DeviceSelector } from './DeviceSelector'
import { devToolsStyles } from './styles'

export function DevTools({
  url = typeof window !== 'undefined' ? window.location.href : '/',
  isOpen,
  onToggle,
}: DevToolsProps) {
  // State
  const [selectedDevices, setSelectedDevices] = useState<Device[]>(essentialDevices)
  const [darkMode, setDarkMode] = useState(false)
  const [showGrid, setShowGrid] = useState(false)
  const [deviceRotations, setDeviceRotations] = useState<Map<string, boolean>>(new Map())
  const [fullscreenDevice, setFullscreenDevice] = useState<Device | null>(null)
  const [showMoreControls, setShowMoreControls] = useState(false)
  const [realDeviceBehavior, setRealDeviceBehavior] = useState(false)
  const [accessibilityMode, setAccessibilityMode] = useState<'none' | 'contrast' | 'colorblind'>(
    'none'
  )

  // Metrics state
  const [performanceData, setPerformanceData] = useState<Map<string, EnhancedLighthouseMetrics>>(
    new Map()
  )
  const [accessibilityData, setAccessibilityData] = useState<
    Map<string, DetailedAccessibilityIssue[]>
  >(new Map())
  const [seoData, setSeoData] = useState<Map<string, ComprehensiveSEOMetrics>>(new Map())
  const [securityData, setSecurityData] = useState<Map<string, SecurityMetrics>>(new Map())
  const [runningTests, setRunningTests] = useState<Set<string>>(new Set())
  const [activeMetricsDevice, setActiveMetricsDevice] = useState<string | null>(null)
  const [metricsExpanded, setMetricsExpanded] = useState(true)
  const [activeTab, setActiveTab] = useState<'performance' | 'accessibility' | 'seo' | 'security'>(
    'performance'
  )

  const moreControlsRef = useRef<HTMLDivElement>(null)

  // Custom hooks
  const { runPerformanceTest } = useDevToolsTests(
    setPerformanceData,
    setAccessibilityData,
    setSeoData,
    setSecurityData,
    setRunningTests,
    setActiveMetricsDevice,
    setActiveTab
  )

  // Effects
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreControlsRef.current && !moreControlsRef.current.contains(event.target as Node)) {
        setShowMoreControls(false)
      }
    }

    if (showMoreControls) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMoreControls])

  // Handlers
  const toggleDeviceRotation = (deviceName: string) => {
    setDeviceRotations(prev => {
      const newMap = new Map(prev)
      newMap.set(deviceName, !prev.get(deviceName))
      return newMap
    })
  }

  const toggleDevice = (device: Device) => {
    setSelectedDevices(prev => {
      const isSelected = prev.find(d => d.name === device.name)
      if (isSelected) {
        return prev.filter(d => d.name !== device.name)
      } else {
        return [...prev, device]
      }
    })
  }

  const loadEssentials = () => {
    setSelectedDevices(essentialDevices)
  }

  if (!isOpen) return null

  const currentMetrics = activeMetricsDevice ? performanceData.get(activeMetricsDevice) : null
  const currentAccessibility = activeMetricsDevice
    ? accessibilityData.get(activeMetricsDevice)
    : null
  const currentSEO = activeMetricsDevice ? seoData.get(activeMetricsDevice) : null
  const currentSecurity = activeMetricsDevice ? securityData.get(activeMetricsDevice) : null

  return (
    <div>
      <style>{devToolsStyles(darkMode)}</style>

      <div className="devtools-container">
        {/* Header */}
        <div className="devtools-header">
          <div className="header-title">DevTools • {selectedDevices.length} devices</div>

          <div className="header-controls">
            <div className="control-group">
              <button onClick={refreshAllFrames} className="control-btn" title="Refresh all">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            <div className="more-controls" ref={moreControlsRef}>
              <button
                onClick={() => setShowMoreControls(!showMoreControls)}
                className={`control-btn ${showMoreControls ? 'active' : ''}`}
                title="More options"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>

              {showMoreControls && (
                <div className="more-dropdown">
                  <div
                    className={`more-item ${darkMode ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDarkMode(!darkMode)}
                    onKeyDown={(e) => e.key === 'Enter' && setDarkMode(!darkMode)}
                  >
                    {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    Dark Mode
                  </div>
                  <div
                    className={`more-item ${showGrid ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowGrid(!showGrid)}
                    onKeyDown={(e) => e.key === 'Enter' && setShowGrid(!showGrid)}
                  >
                    <Grid className="w-4 h-4" />
                    Grid Overlay
                  </div>
                  <div
                    className={`more-item ${realDeviceBehavior ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setRealDeviceBehavior(!realDeviceBehavior)}
                    onKeyDown={(e) => e.key === 'Enter' && setRealDeviceBehavior(!realDeviceBehavior)}
                  >
                    <Smartphone className="w-4 h-4" />
                    Real Device UI
                  </div>
                  <div
                    className={`more-item ${accessibilityMode !== 'none' ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      setAccessibilityMode(
                        accessibilityMode === 'none'
                          ? 'contrast'
                          : accessibilityMode === 'contrast'
                            ? 'colorblind'
                            : 'none'
                      )
                    }
                    onKeyDown={(e) => e.key === 'Enter' && setAccessibilityMode(
                      accessibilityMode === 'none'
                        ? 'contrast'
                        : accessibilityMode === 'contrast'
                          ? 'colorblind'
                          : 'none'
                    )}
                  >
                    <Accessibility className="w-4 h-4" />
                    A11y Filters ({accessibilityMode})
                  </div>
                  <div
                    className="more-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedDevices([])
                      setShowMoreControls(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setSelectedDevices([])
                        setShowMoreControls(false)
                      }
                    }}
                  >
                    <X className="w-4 h-4" />
                    Clear All
                  </div>
                </div>
              )}
            </div>

            <button onClick={onToggle} className="control-btn" title="Close DevTools">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="content-wrapper">
          {/* Sidebar */}
          <DeviceSelector
            devices={devices}
            selectedDevices={selectedDevices}
            deviceRotations={deviceRotations}
            onToggleDevice={toggleDevice}
            onLoadEssentials={loadEssentials}
            onFullscreen={setFullscreenDevice}
          />

          {/* Preview Area */}
          <div className="preview-area">
            <div className="preview-content">
              {selectedDevices.length === 0 ? (
                <div className="empty-state">
                  <Monitor className="w-12 h-12" style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p style={{ fontSize: '16px', marginBottom: '6px', fontWeight: 500 }}>
                    No devices selected
                  </p>
                  <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '12px' }}>
                    Choose devices or load essentials
                  </p>
                  <button onClick={loadEssentials} className="preset-btn">
                    Load Essentials
                  </button>
                </div>
              ) : (
                <div className="preview-grid">
                  {selectedDevices.map(device => (
                    <DevicePreview
                      key={device.name}
                      device={device}
                      url={url}
                      deviceRotations={deviceRotations}
                      performanceData={performanceData}
                      runningTests={runningTests}
                      realDeviceBehavior={realDeviceBehavior}
                      accessibilityMode={accessibilityMode}
                      showGrid={showGrid}
                      onToggleRotation={toggleDeviceRotation}
                      onRemove={() => toggleDevice(device)}
                      onFullscreen={() => setFullscreenDevice(device)}
                      onRunTest={runPerformanceTest}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Metrics Panel */}
        {(currentMetrics || currentAccessibility || currentSEO || currentSecurity) && activeMetricsDevice && (
          <MetricsPanel
            deviceName={activeMetricsDevice}
            isExpanded={metricsExpanded}
            activeTab={activeTab}
            performanceData={currentMetrics || undefined}
            accessibilityData={currentAccessibility || undefined}
            seoData={currentSEO || undefined}
            securityData={currentSecurity || undefined}
            onToggleExpand={() => setMetricsExpanded(!metricsExpanded)}
            onClose={() => setActiveMetricsDevice(null)}
            onTabChange={setActiveTab}
          />
        )}

        {/* Fullscreen Modal */}
        {fullscreenDevice && (
          <div className="fullscreen-overlay">
            <div className="fullscreen-header">
              <div>
                <div className="device-title">
                  {fullscreenDevice.icon}
                  {fullscreenDevice.name}
                  <span style={{ marginLeft: '12px', opacity: 0.7, fontSize: '13px' }}>
                    {deviceRotations.get(fullscreenDevice.name)
                      ? fullscreenDevice.height
                      : fullscreenDevice.width}{' '}
                    ×{' '}
                    {deviceRotations.get(fullscreenDevice.name)
                      ? fullscreenDevice.width
                      : fullscreenDevice.height}
                  </span>
                </div>
              </div>

              <div className="header-controls">
                <button
                  onClick={() => toggleDeviceRotation(fullscreenDevice.name)}
                  className={`control-btn ${deviceRotations.get(fullscreenDevice.name) ? 'active' : ''}`}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button onClick={() => setFullscreenDevice(null)} className="control-btn">
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="fullscreen-viewport">
              {(() => {
                const isRotated = deviceRotations.get(fullscreenDevice.name) || false
                const deviceWidth = isRotated ? fullscreenDevice.height : fullscreenDevice.width
                const deviceHeight = isRotated ? fullscreenDevice.width : fullscreenDevice.height

                const viewportWidth = window.innerWidth - 40
                const viewportHeight = window.innerHeight - 100

                const scaleX = viewportWidth / deviceWidth
                const scaleY = viewportHeight / deviceHeight
                const scale = Math.min(scaleX, scaleY)

                const finalWidth = deviceWidth * scale
                const finalHeight = deviceHeight * scale

                return (
                  <iframe
                    src={getCleanUrl(url)}
                    title={`${fullscreenDevice.name} preview in fullscreen mode`}
                    style={{
                      width: finalWidth,
                      height: finalHeight,
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
                    }}
                  />
                )
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
